<template>
  <div class="p-4">
    <div class="flow-root">
      <ul role="list" class="-mb-8">
        <li v-for="(activityItem, index) in activityItems" :key="activityItem.id">
          <div class="relative pb-8">
            <span v-if="(index + 1) != activityItems.length" class="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
            <div class="relative flex items-start space-x-3">
              <div>
                <div class="relative px-1">
                  <div class="h-7 w-7 bg-gray-100 rounded-full ring-8 ring-white flex items-center justify-center">
                    <img class="h-7 w-7 rounded-full" :src="activityItem.relationships.created_by ? activityItem.relationships.created_by.avatar_url : ''" alt="" />
                  </div>
                </div>
              </div>
              <div class="min-w-0 flex-1">
                <div v-if="activityItem.category == 'comment'" ref="comment">
                  <div>
                    <div class="text-sm">
                      <a class="font-medium text-gray-900"> {{ setUpCreatedByName(activityItem.relationships.created_by) }} </a>
                    </div>
                    <p class="mt-0.5 text-sm text-gray-500"> {{ setUpCommentString(activityItem) }} </p>
                  </div>
                  <div :class="index == active ? 'hidden' : activeClass" class="mt-2 text-sm text-gray-700">
                    <p @click="toggleCommentElem(index, activityItem.comment_text, activityItem)" :style="(activityItem.created_by_id == userId) || isAdmin ? 'cursor: pointer;' : ''"> 
                      <span class="original-field">{{ activityItem.comment_text }}</span>
                      <span class="translated-field hidden">{{ activityItem.comment_text_translated }}</span>
                    </p>
                  </div>


                  <div :class="index == active ? activeClass : 'hidden'" class="pt-4 flex items-start space-x-4">
                    <div class="min-w-0 flex-1">
                      <div class="relative">
                        <div class="border border-gray-300 rounded-lg shadow-sm overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                          <textarea v-model="editedCommentText" rows="3" name="comment" id="comment" class="block w-full py-3 border-0 resize-none focus:ring-0 sm:text-sm" :placeholder="$t('issues.add_comment')" />

                          <!-- Spacer element to match the height of the toolbar -->
                          <div class="py-2" aria-hidden="true">
                            <!-- Matches height of button in toolbar (1px border + 36px content height) -->
                            <div class="py-px">
                              <div class="h-9"></div>
                            </div>
                          </div>
                        </div>

                        <div class="absolute bottom-0 inset-x-0 pl-3 pr-2 py-2 flex justify-between">
                          <div>
                            <button @click="deleteComment(index, activityItem)" type="submit" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">{{ $t('issues.delete_button_text') }}</button>
                          </div>

                          <div>
                            <button @click="editComment(index, editedCommentText, activityItem)" type="submit" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">{{ $t('issues.edit_button_text') }}</button>
                            <button @click="active = null" type="submit" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">{{ $t('issues.cancel_button_text') }}</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="activityItem.category == 'state_change'" ref="state_change">
                  <div v-if="activityItem.state_change_text == 'under_review'" class="text-sm text-gray-500">
                    <a class="font-medium text-gray-900"> {{ setUpCreatedByName(activityItem.relationships.created_by) }} </a>
                    {{ $t('issues.activity_item_under_review', { time_elapsed: timeElapsedSinceCreated(activityItem) }) }}
                  </div>

                  <div v-if="activityItem.state_change_text == 'reopened'" class="text-sm text-gray-500">
                    <a class="font-medium text-gray-900"> {{ setUpCreatedByName(activityItem.relationships.created_by) }} </a>
                    {{ $t('issues.activity_item_open', { time_elapsed: timeElapsedSinceCreated(activityItem) }) }}
                  </div>

                  <div v-if="activityItem.state_change_text == 'closed'" class="text-sm text-gray-500">
                    <a class="font-medium text-gray-900"> {{ setUpCreatedByName(activityItem.relationships.created_by) }} </a>
                    {{ $t('issues.activity_item_closed', { time_elapsed: timeElapsedSinceCreated(activityItem) }) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <div class="pt-4 flex items-start space-x-4">
      <div class="min-w-0 flex-1">
        <div class="relative">
          <div class="border border-gray-300 rounded-lg shadow-sm overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
            <label for="comment" class="sr-only"> {{ $t('issues.add_comment_label') }} </label>
            <textarea v-on:keyup.enter="postComment()" v-model="newCommentText" rows="3" name="comment" id="comment" class="block w-full py-3 border-0 resize-none focus:ring-0 sm:text-sm" :placeholder="$t('issues.add_comment')" />

            <!-- Spacer element to match the height of the toolbar -->
            <div class="py-2" aria-hidden="true">
              <!-- Matches height of button in toolbar (1px border + 36px content height) -->
              <div class="py-px">
                <div class="h-9"></div>
              </div>
            </div>
          </div>

          <div class="absolute bottom-0 inset-x-0 pl-3 pr-2 py-2 flex justify-end">
            <div class="flex-shrink-0">
              <button @click="postComment()" type="submit" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">{{ $t('issues.post_button_text') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios'

  export default {
    name: '',
    data () {
      return {
        activityItems: null,
        newCommentText: null,
        editedCommentText: null,
        hideEditComment: true,
        active: null,
        activeClass: 'is-visible',
      }
    },
    created () {
      this.activityItems = this.issue.relationships.issue_activity_items
      console.log("this.activityItems", this.activityItems)
    },
    computed: {
    },
    methods: {
      timeSince(date) {
        let seconds = Math.floor((new Date() - date) / 1000)

        let interval = seconds / 31536000

        if (interval > 1) {
          return Math.floor(interval) == 1 ? (Math.floor(interval) + ` ${this.$t('issues.year')}`) : (Math.floor(interval) + ` ${this.$t('issues.years')}`)
        }
        interval = seconds / 2592000
        if (interval > 1) {
          return Math.floor(interval) == 1 ? (Math.floor(interval) + ` ${this.$t('issues.month')}`) : (Math.floor(interval) + ` ${this.$t('issues.months')}`)
        }
        interval = seconds / 86400
        if (interval > 1) {
          return Math.floor(interval) == 1 ? (Math.floor(interval) + ` ${this.$t('issues.day')}`) : (Math.floor(interval) + ` ${this.$t('issues.days')}`)
        }
        interval = seconds / 3600
        if (interval > 1) {
          return Math.floor(interval) == 1 ? (Math.floor(interval) + ` ${this.$t('issues.hour')}`) : (Math.floor(interval) + ` ${this.$t('issues.hours')}`)
        }
        interval = seconds / 60
        if (interval > 1) {
          return Math.floor(interval) == 1 ? (Math.floor(interval) + ` ${this.$t('issues.minute')}`) : (Math.floor(interval) + ` ${this.$t('issues.minutes')}`)
        }

        return Math.floor(seconds) == 1 ? (Math.floor(seconds) + ` ${this.$t('issues.second')}`) : (Math.floor(seconds) + ` ${this.$t('issues.seconds')}`)
      },
      timeElapsedSinceCreated (activityItem) {
        return `${this.timeSince(activityItem.created_at_lux)}`
      },
      postComment () {
        let issueActivityItem = {
          category: "comment",
          comment_text: this.newCommentText,
          state_change_text: null,
          created_by_id: this.userId,
          issue_id: this.issue.id,
        }

        if (this.newCommentText == null || this.newCommentText.trim().length === 0) {
          return -1
        } else {
          axios.post(`/issue_activity_items`, {
            issue_activity_item: issueActivityItem,
          }, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            }
          })
          .then(response => {
            console.log("The data", response)
  
            this.newCommentText = null
  
            this.$emit("issue-activity-item-creation", {
              issue_activity_item: issueActivityItem,
            })
  
            return response
          }, error => {
            window.Rollbar.error('error creating issue activity item comment', { error: error })
            console.log('error when creating issue activity item comment: ', error)
          })
        }
      },
      toggleCommentElem (index, text, comment) {
        if ((comment.created_by_id == this.userId) || this.isAdmin) {
          this.active = index
          this.editedCommentText = text
        }
      },
      editComment (index, text, comment) {
        console.log("editing", text)
        console.log("comment", comment)

        this.active = null

        axios.patch(`/issue_activity_items/${comment.id}`, {
          issue_activity_item: {
            id: comment.id,
            comment_text: text
          },
        }, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          }
        })
        .then(response => {
          this.$emit("issue-activity-item-creation", {
            issue_activity_item: comment,
          })
          
          return response
        }, error => {
          window.Rollbar.error('error editing issue activity item comment', { error: error })
          console.log('error when editing issue activity item comment: ', error)
        })
      },
      deleteComment(index, comment) {
        console.log("comment to delete: ", comment)

        let result = confirm(this.$t('issues.comment_deletion_confirmation'))
        if (result) {
          axios.delete(`/issue_activity_items/${comment.id}`, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            }
          })
          .then(response => {
            console.log("The data", response)

            this.$emit("issue-activity-item-creation", {
              issue_activity_item: comment,
            })

            return response
          }, error => {
            console.log('error when deleting issue activity item comment: ', error)
          })
        }
      },
      setUpCreatedByName (obj) {
        return `${obj.first_name} ${obj.last_name}`
      },
      setUpCommentString (activityItem) {
        return this.$t('issues.activity_item_comment', { time_elapsed: this.timeElapsedSinceCreated(activityItem) })
      },
    },
    watch: { 
      stateChanged: function(newVal, oldVal) {
        let issueActivityItem = {
          category: "state_change",
          comment_text: null,
          state_change_text: newVal,
          created_by_id: this.userId,
          issue_id: this.issue.id,
          created_at_lux: new Date(),

          relationships: {
            created_by: {
              first_name: this.userName.split(" ")[0],
              last_name: this.userName.split(" ")[1],
              avatar_url: this.avatarURL
            }
          }
        }

        this.activityItems.push(issueActivityItem)

        // // TODO: maybe use below to avoid doing "hacking" above.
        // this.$emit("issue-activity-item-creation", {
        //   issue_activity_item: issueActivityItem,
        // })
      },
      issue: function(newVal, oldVal) {
        console.log('changed', newVal, oldVal)

        this.activityItems = newVal.relationships.issue_activity_items.sort((item1, item2) => item1.created_at_lux - item2.created_at_lux)
        this.activityItems = this.activityItems.filter(i => i.discarded_at == null)
      },
    },
    props: {
      issue: {
        type: Object,
        required: true,
      },
      userId: {
        type: Number,
        required: false,
      },
      userName: {
        type: String,
        required: false
      },
      stateChanged: {
        type: String,
        required: false
      },
      isAdmin: {
        type: Boolean,
        required: false
      },
      avatarURL: {
        type: String,
        required: false,
      }
    },
  }
</script>

<style lang="scss">
</style>
